import { Bot, User, Wrench } from "lucide-react";
import { useState } from "react";

import { ConvoSplitView } from "@/components/admin/agenti/ConvoSplitView";
import { ThreadExportButtons } from "@/components/admin/agenti/ThreadExportButtons";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useAgenteChatConversazioni,
  useAgenteChatTranscript,
} from "@/hooks/useAgenteChatAdmin";
import { cn } from "@/lib/utils";

export function CanaleChat({ slug }: { slug: string }) {
  const lista = useAgenteChatConversazioni();
  const [sel, setSel] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const transcript = useAgenteChatTranscript(sel);

  const conversazioni = (lista.data?.conversazioni ?? []).filter(
    (c) => !q || (c.utente ?? "").toLowerCase().includes(q.toLowerCase()),
  );

  const listaNode = (
    <>
      <div className="border-b bg-card p-2.5">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Cerca nelle chat…"
          className="w-full rounded-md border px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-700/30"
        />
      </div>
      {lista.isLoading ? (
        <div className="p-3">
          <Skeleton className="h-24 w-full" />
        </div>
      ) : conversazioni.length === 0 ? (
        <p className="p-4 text-sm text-muted-foreground">Nessuna conversazione in chat.</p>
      ) : (
        conversazioni.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => setSel(c.id)}
            className={cn(
              "block w-full border-b px-3 py-2.5 text-left hover:bg-card",
              sel === c.id && "border-l-[3px] border-l-brand-700 bg-brand-50",
            )}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="truncate text-[13px] font-semibold">{c.utente}</span>
              <span className="shrink-0 text-[11px] text-muted-foreground">
                {c.ultimo_messaggio_il
                  ? new Date(c.ultimo_messaggio_il).toLocaleDateString("it-IT")
                  : ""}
              </span>
            </div>
            <div className="text-xs text-muted-foreground">{c.n_messaggi} messaggi</div>
          </button>
        ))
      )}
    </>
  );

  const dettaglioNode = !sel ? (
    <div className="grid flex-1 place-items-center p-8 text-sm text-muted-foreground">
      Seleziona una conversazione per vederne la trascrizione.
    </div>
  ) : transcript.isLoading ? (
    <div className="p-4">
      <Skeleton className="h-64 w-full" />
    </div>
  ) : transcript.data ? (
    <>
      <div className="flex items-center gap-3 border-b px-4 py-3">
        <div className="min-w-0">
          <div className="text-sm font-semibold">Trascrizione chat</div>
          <div className="text-xs text-muted-foreground">
            {transcript.data.messaggi.length} messaggi · $
            {transcript.data.costo_usd.toFixed(4)}
          </div>
        </div>
        <div className="ml-auto">
          <ThreadExportButtons slug={slug} canale="chat" threadId={sel} />
        </div>
      </div>
      <div className="max-h-[560px] space-y-4 overflow-y-auto p-4">
        {transcript.data.messaggi.map((m) => (
          <div key={m.id} className="space-y-1">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
              {m.ruolo === "utente" ? <User className="size-3.5" /> : <Bot className="size-3.5" />}
              {m.ruolo === "utente" ? "Utente" : "Aida"}
              <span className="font-normal">
                · {new Date(m.creato_il).toLocaleString("it-IT")}
              </span>
            </div>
            <div className="whitespace-pre-wrap rounded-lg border bg-muted/30 px-3 py-2 text-sm">
              {m.testo || <span className="text-muted-foreground">—</span>}
            </div>
            {m.passi.length > 0 && (
              <div className="ml-2 space-y-1 border-l-2 pl-3">
                {m.passi.map((p, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-1.5 text-xs text-muted-foreground"
                  >
                    <Wrench className="mt-0.5 size-3 shrink-0" />
                    <span>
                      <span className="font-mono">{p.nome}</span>
                      {p.args && Object.keys(p.args).length > 0
                        ? ` (${JSON.stringify(p.args)})`
                        : ""}{" "}
                      → {p.esito}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  ) : (
    <div className="p-4 text-sm text-muted-foreground">Impossibile caricare la trascrizione.</div>
  );

  return <ConvoSplitView lista={listaNode} dettaglio={dettaglioNode} />;
}
